import React, { useEffect, useState } from 'react'

/**
 * Minimal shared toast/banner system (M-FE / err-18): replaces the blocking
 * `alert()` calls so users get a non-blocking, self-dismissing message.
 * `showToast` is a plain function so non-React code (e.g. util/api.js) can
 * raise toasts too. <ToastHost /> renders them; mounted once at the root.
 */

export type ToastKind = 'error' | 'success' | 'info'

interface Toast {
	id: number
	kind: ToastKind
	message: string
}

type Listener = (toasts: Toast[]) => void

let toasts: Toast[] = []
let nextId = 1
const listeners = new Set<Listener>()

const notify = () => listeners.forEach(l => { try { l(toasts) } catch {} })

export const showToast = (message: string, kind: ToastKind = 'error') => {
	const toast = { id: nextId++, kind, message }
	toasts = [...toasts, toast]
	notify()
	setTimeout(() => dismissToast(toast.id), 6000)
}

const dismissToast = (id: number) => {
	toasts = toasts.filter(t => t.id !== id)
	notify()
}

const KIND_COLORS: Record<ToastKind, string> = {
	error: '#b23434',
	success: '#2c7a3f',
	info: 'var(--blue, #263b9f)',
}

export const ToastHost: React.FC = () => {
	const [items, setItems] = useState<Toast[]>(toasts)

	useEffect(() => {
		const listener: Listener = t => setItems(t)
		listeners.add(listener)
		return () => { listeners.delete(listener) }
	}, [])

	if (items.length === 0) return null
	return (
		<div style={{
			position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)',
			zIndex: 10000, display: 'flex', flexDirection: 'column', gap: '8px',
			maxWidth: 'min(560px, 90vw)',
		}}>
			{items.map(t => (
				<div
					key={t.id}
					onClick={() => dismissToast(t.id)}
					role="alert"
					style={{
						background: KIND_COLORS[t.kind], color: '#fff', padding: '12px 20px',
						borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
						fontSize: '15px', fontWeight: 500, cursor: 'pointer', textAlign: 'center',
					}}
				>
					{t.message}
				</div>
			))}
		</div>
	)
}
